import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import $ from 'jquery';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as action from '../../Action/Curation/Curation_Action';
import { getSeries } from '../../Action/Series/Series_Action';
import * as Authservices from '../../Services/services';
import {
    CURATION_TYPE,
    HOLD_DAYS,
    NAME_MAX_LENGTH,
    RAIL_SIZE,
    applyMove,
    describeMoves,
    draftPreview,
    holdPositions,
    holdTimeLeft,
    misplacedPins,
    normaliseCuration,
    pinAt,
    pinPositionOf,
    pinPositions,
    planChange,
    planMoves,
    planReleaseHold,
    validateRailName,
} from '../../utils/newOnHikeCinemaCuration';
import { usePermission } from '../../hooks/usePermission';
import { PERM } from '../../config/permissions';
import { CuratedCollectionStyles } from './CuratedCollection';

/**
 * New on Hike Cinema — the recent-releases rail, shown as a tab on /curated.
 *
 * The rail fills itself with the latest RAIL_SIZE go-lives, newest first. Admins
 * only manage overrides, via GET/PATCH /admin/v1/curation/curate-collections?curation_type=new_release:
 *   pin      permanent, at the exact position chosen (`pins`)
 *   hold     temporary; fixes a series at a position for 15 days (`reorder`)
 *   exclude  keeps a series out of the rail and auto-fill (`excluded_ids`)
 *   reset    clears all three at once
 *
 * Dragging a row in the rail moves it: a pin stays a pin at its new position
 * (dropping it on another pin swaps the two); any other row becomes a 15-day
 * hold there. Drags only change a local draft; Save & Publish sends them all
 * in one PATCH, and every other edit is locked until they are saved or discarded.
 *
 * Every other action is its own PATCH and takes effect immediately — the response is
 * the saved rail, preview included, so the screen just applies it. Building the
 * body (and refusing bodies the backend would reject) lives in
 * utils/newOnHikeCinemaCuration.js.
 */

const DEFAULT_NAME = 'New on Hike Cinema';

// Picker reuses the curated-collection series search (active series only).
const SEARCH_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

const EMPTY_RAIL = normaliseCuration({});

/** Unwraps the shared response envelope: { success, message, data, ... } → data. */
const unwrap = (res) => (res?.data !== undefined ? res.data : res?.responseDetails);

const readMessage = (res, fallback) => res?.responseMessage || res?.message || fallback;

const readError = (err, fallback) => {
    const details = Array.isArray(err?.details) && err.details.length > 0
        ? err.details.map((d) => d?.message || d).join('; ')
        : null;
    return details || readMessage(err, fallback);
};

const toPreview = (rows) => (Array.isArray(rows) ? rows : [])
    .map((row) => ({
        seriesId: Number(row?.series_id),
        position: Number(row?.position),
        title: row?.series_title || row?.title || '',
        thumbnail: row?.series_thumbnail || row?.thumbnail || '',
        isPinned: row?.is_pinned === true || row?.is_pinned === 1,
        isHeld: row?.is_reordered === true || row?.is_reordered === 1,
        expiresAt: row?.expires_at || null,
        publishAt: row?.publish_at || null,
    }))
    .filter((r) => Number.isFinite(r.seriesId) && Number.isFinite(r.position))
    .sort((a, b) => a.position - b.position);

/** A search hit from /series/get: here `id` IS the series id. */
const fromSearchResult = (row) => ({
    seriesId: Number(row?.id),
    title: row?.title || '',
    thumbnail: row?.thumbnail || '',
    // null = unknown (the endpoint didn't say), so nothing is blocked on it.
    hasPoster: row?.foreground_poster === undefined ? null : Boolean(row.foreground_poster),
    // is_active comes back as 1/0 or true/false depending on the row's age.
    isActive: row?.is_active === undefined ? true : Boolean(Number(row.is_active)),
});

const NO_POSTER_ERROR = 'This series has no New on Hike Cinema thumbnail. Add one on the series first.';

const isDate = (value) => Number.isFinite(Date.parse(value));

const formatDate = (value) => new Date(value).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
});

const formatDateTime = (value) => new Date(value).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

const holdLabel = (expiresAt) => {
    const left = holdTimeLeft(expiresAt);
    if (!left) return `${HOLD_DAYS}-day hold`;
    if (left.expired) return 'Hold expired · clears at the next go-live';
    return `${left.daysLeft} day${left.daysLeft === 1 ? '' : 's'} left · ends ${formatDateTime(expiresAt)}`;
};

// fa-thumbtack-slash is Font Awesome Pro; the free set only has the two parts.
const UnpinIcon = () => (
    <span className="fa-stack nohc-unpin" aria-hidden="true">
        <i className="fas fa-thumbtack fa-stack-1x"></i>
        <i className="fas fa-slash fa-stack-1x"></i>
    </span>
);

export const Thumb = ({ src, small }) => (
    <span className={`cc-thumb${small ? ' cc-thumb-sm' : ''}`}>
        <i className="fas fa-film"></i>
        {src && (
            <img
                src={src}
                alt=""
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
        )}
    </span>
);

/** One filled slot of the rail; the handle drags it to another slot. */
const SortableRailRow = ({ seriesId, allowEdit, busy, className, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: seriesId, disabled: !allowEdit || busy });

    return (
        <div
            ref={setNodeRef}
            className={`cc-row ${className}${isDragging ? ' is-dragging' : ''}`}
            style={{ transform: CSS.Transform.toString(transform), transition }}
        >
            {allowEdit && (
                <button
                    type="button"
                    className="cc-handle"
                    title="Drag to move — a pin stays pinned, anything else is held there for 15 days"
                    disabled={busy}
                    {...attributes}
                    {...listeners}
                >
                    <i className="fas fa-grip-vertical"></i>
                </button>
            )}
            {children}
        </div>
    );
};

const NewOnHikeCinema = ({ onNameChange }) => {
    const dispatch = useDispatch();
    const { canEdit } = usePermission();
    const allowEdit = canEdit(PERM.SERIES);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    // True when GET 404s: the route (or this curation_type) is not deployed yet.
    const [apiMissing, setApiMissing] = useState(false);
    // Key of the write in flight; every edit control is locked while it is set
    // so two saves from this tab never race each other.
    const [busyKey, setBusyKey] = useState(null);
    const busy = busyKey !== null;

    const [rail, setRail] = useState(EMPTY_RAIL);
    const [preview, setPreview] = useState([]);
    // Unsaved drags: the rail as it would be after Save & Publish, or null.
    const [draft, setDraft] = useState(null);
    const editLocked = busy || draft !== null;
    const [updatedAt, setUpdatedAt] = useState(null);
    const [nameDraft, setNameDraft] = useState(DEFAULT_NAME);
    const savedNameRef = useRef(DEFAULT_NAME);

    // Title/thumbnail by series id. The API only gives ids for pins, holds and
    // exclusions, so these come from the preview, search hits and — for excluded
    // series, which never appear in the preview — the series details endpoint.
    const [seriesInfo, setSeriesInfo] = useState({});

    const rememberSeries = useCallback((rows) => {
        setSeriesInfo((prev) => {
            const next = { ...prev };
            rows.forEach((r) => {
                if (r.title) {
                    next[r.seriesId] = { title: r.title, thumbnail: r.thumbnail || prev[r.seriesId]?.thumbnail || '' };
                }
            });
            return next;
        });
    }, []);

    const infoOf = useCallback((seriesId) => ({
        title: seriesInfo[seriesId]?.title || `Series #${seriesId}`,
        thumbnail: seriesInfo[seriesId]?.thumbnail || '',
    }), [seriesInfo]);

    const applyData = useCallback((data) => {
        const next = normaliseCuration(data);
        const rows = toPreview(data?.preview);
        const nextName = next.name || DEFAULT_NAME;
        // Keep a rename the admin is still typing; otherwise follow the server.
        setNameDraft((draft) => (draft.trim() === savedNameRef.current.trim() ? nextName : draft));
        savedNameRef.current = nextName;
        setRail(next);
        setPreview(rows);
        setUpdatedAt(data?.updated_at || null);
        rememberSeries(rows);
    }, [rememberSeries]);

    // `silent` is for resyncs after a failed write, whose error is already on screen.
    const fetchData = useCallback((initial = false, silent = false) => {
        if (initial) setLoading(true); else setRefreshing(true);
        return dispatch(action.getCuration(CURATION_TYPE))
            .then((res) => {
                setApiMissing(false);
                applyData(unwrap(res));
            })
            .catch((err) => {
                if (err?.status === 404) setApiMissing(true);
                if (!silent) {
                    toast.error(readMessage(err, 'Failed to load New on Hike Cinema.'), { toastId: 'nohc-load' });
                }
            })
            .finally(() => { setLoading(false); setRefreshing(false); });
    }, [dispatch, applyData]);

    useEffect(() => { fetchData(true); }, [fetchData]);

    useEffect(() => { if (rail.name) onNameChange?.(rail.name); }, [rail.name, onNameChange]);

    // Fill in titles for override rows the preview didn't cover (mostly excluded series).
    const requestedInfo = useRef(new Set());
    useEffect(() => {
        const ids = [...rail.seriesIds, ...rail.holds.map((h) => h.seriesId), ...rail.excludedIds];
        ids.filter((id) => !seriesInfo[id]?.title && !requestedInfo.current.has(id)).forEach((id) => {
            requestedInfo.current.add(id);
            Authservices.getSeriesDetails({ series_id: id })
                .then((res) => {
                    const details = res?.data?.responseDetails || res?.data?.data;
                    if (details?.title) {
                        rememberSeries([{ seriesId: id, title: details.title, thumbnail: details.thumbnail || '' }]);
                    }
                })
                .catch(() => {});
        });
    }, [rail, seriesInfo, rememberSeries]);

    // What the screen shows: the unsaved draft when there is one, else the saved rail.
    const view = draft ?? rail;
    const viewPreview = useMemo(() => (draft ? draftPreview(draft, preview) : preview), [draft, preview]);
    const moves = useMemo(() => (draft ? describeMoves(rail, draft) : []), [rail, draft]);
    const movedIds = useMemo(() => new Set(moves.map((m) => m.seriesId)), [moves]);

    const pinnedSet = useMemo(() => new Set(view.seriesIds), [view.seriesIds]);
    const excludedSet = useMemo(() => new Set(view.excludedIds), [view.excludedIds]);
    const holdById = useMemo(() => new Map(view.holds.map((h) => [h.seriesId, h])), [view.holds]);
    const holdByPosition = useMemo(() => new Map(view.holds.map((h) => [h.position, h])), [view.holds]);
    const previewById = useMemo(() => new Map(viewPreview.map((p) => [p.seriesId, p])), [viewPreview]);
    const previewByPosition = useMemo(() => new Map(viewPreview.map((p) => [p.position, p])), [viewPreview]);

    const hasOverrides = rail.pins.length + rail.excludedIds.length + rail.holds.length > 0;

    // Leaving the page would lose the unsaved drags.
    useEffect(() => {
        if (!draft) return undefined;
        const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [draft]);

    // ─── Writes ──────────────────────────────────────────────────────────────

    /**
     * Confirms any 15-day holds a planned change would end, sends its PATCH and
     * applies the saved rail from the response.
     * @returns {Promise<boolean>} whether the change was saved.
     */
    const sendPlan = useCallback((key, plan, successMessage, { confirmEvictions = true } = {}) => {
        const send = () => {
            setBusyKey(key);
            return dispatch(action.patchCuration(plan.body))
                .then((res) => {
                    const data = unwrap(res);
                    // The pins we sent are the ranks the admin picked. If the API
                    // stored one somewhere else (it keeps pins as a block from #1
                    // until it supports exact positions), say so — never report a
                    // rank that isn't what the rail now holds.
                    const misplaced = misplacedPins(plan.body, data);
                    if (misplaced) toast.error(misplaced, { toastId: `nohc-${key}` });
                    else toast.success(successMessage);
                    if (data && typeof data === 'object' && Array.isArray(data.series_ids)) {
                        applyData(data);
                        return true;
                    }
                    return fetchData(false, true).then(() => true);
                })
                .catch((err) => {
                    toast.error(readError(err, 'Failed to update New on Hike Cinema.'), { toastId: `nohc-${key}` });
                    // 409 = stale config or a pin in the way; resync quietly either way.
                    fetchData(false, true);
                    return false;
                })
                .finally(() => setBusyKey(null));
        };

        if (!confirmEvictions || plan.evicted.length === 0) return send();

        const names = plan.evicted
            .map((h) => `"${infoOf(h.seriesId).title}" at #${h.position}`)
            .join(', ');
        return Swal.fire({
            title: 'End a 15-day hold?',
            text: `This change takes over the slot held by ${names}, ending that hold early.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--first-color)',
            cancelButtonColor: 'var(--error-color)',
            confirmButtonText: 'Yes, continue',
            customClass: { confirmButton: 'btn', cancelButton: 'btn' },
        }).then((result) => (result.isConfirmed ? send() : false));
    }, [dispatch, applyData, fetchData, infoOf]);

    /** Plans one immediate change against the saved rail and sends it. */
    const commit = useCallback((key, intent, successMessage) => {
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return Promise.resolve(false);
        }
        const plan = planChange(rail, intent);
        if (plan.error) {
            toast.error(plan.error, { toastId: 'nohc-plan' });
            return Promise.resolve(false);
        }
        return sendPlan(key, plan, successMessage, { confirmEvictions: intent.type !== 'reset' });
    }, [allowEdit, rail, sendPlan]);

    /** Save & Publish: every staged drag in one PATCH. The draft is kept if it fails. */
    const saveMoves = () => {
        if (!draft) return;
        const plan = planMoves(rail, draft);
        if (plan.error) {
            toast.error(plan.error, { toastId: 'nohc-plan' });
            return;
        }
        if (!plan.body) {
            setDraft(null);
            return;
        }
        const count = moves.length;
        sendPlan('save-moves', plan, `${count} position change${count === 1 ? '' : 's'} published.`)
            .then((ok) => { if (ok) setDraft(null); });
    };

    const discardMoves = () => setDraft(null);

    const pinSeries = (seriesId, position) => commit(
        `pin-${seriesId}`,
        { type: 'pin', seriesId, position },
        `"${infoOf(seriesId).title}" pinned at #${position}.`,
    );


    const unpinSeries = (seriesId) => commit(
        `unpin-${seriesId}`,
        { type: 'unpin', seriesId },
        `"${infoOf(seriesId).title}" unpinned — its slot is back on auto-fill.`,
    );

    const holdSeries = (seriesId, position, restart = false) => commit(
        `hold-${seriesId}`,
        { type: 'hold', seriesId, position },
        restart
            ? `Hold on "${infoOf(seriesId).title}" restarted — ${HOLD_DAYS} days from now.`
            : `"${infoOf(seriesId).title}" held at #${position} for ${HOLD_DAYS} days.`,
    );

    /**
     * Ends a hold early: the series goes back to auto-fill instead of sitting at
     * a fixed slot. One PATCH carrying `remove_ids` — see planReleaseHold.
     */
    const releaseHold = (seriesId) => {
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return Promise.resolve(false);
        }
        const { title } = infoOf(seriesId);
        const { body, error } = planReleaseHold(rail, seriesId);
        if (error) {
            toast.error(error, { toastId: 'nohc-plan' });
            return Promise.resolve(false);
        }
        return Swal.fire({
            title: `Release "${title}" from its hold?`,
            text: 'It stops sitting at a fixed position and goes back to auto-fill, so newer releases can push it down the rail. The series itself stays live in the app.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--first-color)',
            cancelButtonColor: 'var(--error-color)',
            confirmButtonText: 'Yes, release it',
            customClass: { confirmButton: 'btn', cancelButton: 'btn' },
        }).then((result) => {
            if (!result.isConfirmed) return false;
            setBusyKey(`release-${seriesId}`);
            return dispatch(action.patchCuration(body))
                .then((res) => {
                    toast.success(`Hold on "${title}" released — the slot is back on auto-fill.`);
                    const data = unwrap(res);
                    if (data && typeof data === 'object' && Array.isArray(data.series_ids)) {
                        applyData(data);
                        return true;
                    }
                    return fetchData(false, true).then(() => true);
                })
                .catch((err) => {
                    toast.error(readError(err, `Failed to release the hold on "${title}".`), { toastId: `nohc-release-${seriesId}` });
                    return fetchData(false, true).then(() => false);
                })
                .finally(() => setBusyKey(null));
        });
    };

    const restoreSeries = (seriesId) => commit(
        `restore-${seriesId}`,
        { type: 'restore', seriesId },
        `"${infoOf(seriesId).title}" is back in the auto-fill pool.`,
    );

    const excludeSeries = (seriesId) => {
        const { title } = infoOf(seriesId);
        return Swal.fire({
            title: `Exclude "${title}" from the rail?`,
            text: 'It leaves the rail and auto-fill skips it until you restore it — any pin or 15-day hold on it ends. The series itself stays live in the app.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--first-color)',
            cancelButtonColor: 'var(--error-color)',
            confirmButtonText: 'Yes, exclude it',
            customClass: { confirmButton: 'btn', cancelButton: 'btn' },
        }).then((result) => {
            if (!result.isConfirmed) return false;
            return commit(`exclude-${seriesId}`, { type: 'exclude', seriesId }, `"${title}" excluded from the rail.`);
        });
    };

    const resetRail = () => {
        const parts = [
            rail.pins.length && `${rail.pins.length} pin${rail.pins.length === 1 ? '' : 's'}`,
            rail.holds.length && `${rail.holds.length} 15-day hold${rail.holds.length === 1 ? '' : 's'}`,
            rail.excludedIds.length && `${rail.excludedIds.length} excluded series`,
        ].filter(Boolean);
        Swal.fire({
            title: 'Reset to pure auto-fill?',
            text: `This clears ${parts.join(', ')}. The rail goes back to the latest ${RAIL_SIZE} go-lives. The name is kept.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--error-color)',
            cancelButtonColor: 'var(--border-color)',
            confirmButtonText: 'Yes, reset',
            customClass: { confirmButton: 'btn', cancelButton: 'btn' },
        }).then((result) => {
            if (!result.isConfirmed) return;
            commit('reset', { type: 'reset' }, 'Rail reset — it is pure auto-fill again.');
        });
    };

    const trimmedName = nameDraft.trim();
    const nameError = validateRailName(trimmedName);
    const nameDirty = trimmedName !== (rail.name || DEFAULT_NAME).trim();

    const toggleActive = (isActive) => commit(
        'active',
        { type: 'setActive', isActive },
        isActive ? 'Rail switched on.' : 'Rail switched off — your pins, holds and exclusions are kept.',
    );

    const handleRename = () => {
        if (nameError) {
            toast.error(nameError);
            return;
        }
        commit('rename', { type: 'rename', name: trimmedName }, 'Rail renamed.');
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    /**
     * Dropping a rail row on another row moves it to that row's position in the
     * draft — nothing is sent until Save & Publish. A pin is re-pinned there
     * (onto another pin = swap); anything else is held there for 15 days. Only
     * a pinned slot can't take a series that isn't pinned.
     */
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const target = previewById.get(over.id)?.position;
        if (!target) return;

        const occupant = pinAt(view, target);
        if (pinPositionOf(view, active.id) === null && occupant) {
            toast.error(`#${target} is pinned by "${infoOf(occupant.seriesId).title}". Unpin it first, or drop the series somewhere else.`, { toastId: 'nohc-drag' });
            return;
        }
        const { state: next, error } = applyMove(view, active.id, target);
        if (error) {
            toast.error(error, { toastId: 'nohc-drag' });
            return;
        }
        // Dragging everything back to where it was leaves nothing to save.
        setDraft(planMoves(rail, next).body ? next : null);
    };

    // ─── Picker ──────────────────────────────────────────────────────────────

    const [showPicker, setShowPicker] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [searchPage, setSearchPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    // How many search hits were left out for having no foreground_poster.
    const [hiddenNoPoster, setHiddenNoPoster] = useState(0);
    const [searchLoading, setSearchLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [picked, setPicked] = useState(null);
    const [pickMode, setPickMode] = useState('hold');
    const [pickPosition, setPickPosition] = useState(null);

    // Guards against a slow early search overwriting a later one's results.
    const searchSeq = useRef(0);

    const runSearch = useCallback((term, page, append) => {
        const seq = searchSeq.current + 1;
        searchSeq.current = seq;

        if (append) setLoadingMore(true); else setSearchLoading(true);

        const params = { page, limit: SEARCH_PAGE_SIZE };
        if (term.trim()) params.search = term.trim();

        // The series list, not the curation series-search: that one returns only
        // id/title/thumbnail, so a series with no foreground_poster can't be told
        // apart from one the endpoint says nothing about. /series/get carries the
        // whole row, which is what makes the poster filter below possible.
        dispatch(getSeries(params))
            .then((res) => {
                if (seq !== searchSeq.current) return;
                const details = res?.responseDetails || {};
                const raw = Array.isArray(details.rows) ? details.rows : (details.data || unwrap(res));
                const found = (Array.isArray(raw) ? raw : [])
                    .map(fromSearchResult)
                    // This list includes inactive series; the rail only takes live
                    // ones, which the curation search used to filter server-side.
                    .filter((row) => Number.isFinite(row.seriesId) && row.isActive);
                rememberSeries(found);
                // The rail card is the series' foreground_poster, and the backend
                // rejects a pin or hold on a series without one — so those are not
                // offered at all. `hasPoster` is null when the search endpoint
                // doesn't report it; unknown is kept, only a known-missing poster
                // is dropped.
                const rows = found.filter((row) => row.hasPoster !== false);
                setHiddenNoPoster((prev) => (append ? prev : 0) + (found.length - rows.length));
                setResults((prev) => {
                    if (!append) return rows;
                    const seen = new Set(prev.map((r) => r.seriesId));
                    return [...prev, ...rows.filter((r) => !seen.has(r.seriesId))];
                });
                setSearchPage(page);
                // Measured on the raw page, not on what survived the active and
                // poster filters, or dropping a few rows would end paging early.
                setHasMore((Array.isArray(raw) ? raw.length : 0) === SEARCH_PAGE_SIZE);
            })
            .catch((err) => {
                if (seq !== searchSeq.current) return;
                toast.error(readMessage(err, 'Failed to search series.'));
                if (!append) setResults([]);
                setHasMore(false);
            })
            .finally(() => {
                if (seq !== searchSeq.current) return;
                setSearchLoading(false);
                setLoadingMore(false);
            });
    }, [dispatch, rememberSeries]);

    useEffect(() => {
        if (!showPicker) return undefined;
        const handle = setTimeout(() => runSearch(searchTerm, 1, false), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [showPicker, searchTerm, runSearch]);

    /**
     * Where a mode starts for a series: the slot it already has (pin, hold, or
     * where auto-fill shows it), else the first slot nothing else is fixed at.
     */
    const defaultPosition = useCallback((mode, seriesId) => {
        if (mode === 'exclude') return null;
        const options = mode === 'pin' ? pinPositions(rail, seriesId) : holdPositions(rail, seriesId);
        const own = pinPositionOf(rail, seriesId) ?? holdById.get(seriesId)?.position ?? previewById.get(seriesId)?.position;
        if (own && options.includes(own)) return own;
        return options.find((p) => !pinAt(rail, p) && !holdByPosition.has(p)) ?? options[0] ?? null;
    }, [rail, holdById, holdByPosition, previewById]);

    const openPicker = () => {
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return;
        }
        setShowPicker(true);
        setSearchTerm('');
        setResults([]);
        setSearchPage(1);
        setHasMore(false);
        setPicked(null);
        setPickMode('hold');
        setPickPosition(defaultPosition('hold'));
        $('body').addClass('no_scroll');
    };

    const closePicker = useCallback(() => {
        setShowPicker(false);
        setPicked(null);
        setSearchTerm('');
        setResults([]);
        $('body').removeClass('no_scroll');
    }, []);

    // Escape closes the picker, like the other dialogs in the panel.
    useEffect(() => {
        if (!showPicker) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !busy) closePicker(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showPicker, busy, closePicker]);

    // A route change while the modal is open would otherwise strand this.
    useEffect(() => () => { $('body').removeClass('no_scroll'); }, []);

    const changeMode = (mode) => {
        setPickMode(mode);
        setPickPosition(defaultPosition(mode, picked?.seriesId));
    };

    /** Clicking the selected series again clears it, back to "nothing picked yet". */
    const pickSeries = (row) => {
        if (picked?.seriesId === row.seriesId) {
            setPicked(null);
            setPickPosition(null);
            return;
        }
        setPicked(row);
        setPickPosition(defaultPosition(pickMode, row.seriesId));
    };

    const clearPick = () => {
        setPicked(null);
        setPickPosition(null);
    };

    const pickedId = picked?.seriesId ?? null;

    // The same planner the write uses, so the modal shows its verdict up front.
    const pickedNoPoster = picked?.hasPoster === false;
    const livePlan = useMemo(() => {
        if (pickedId === null) return null;
        if (pickMode === 'exclude') return planChange(rail, { type: 'exclude', seriesId: pickedId });
        // The backend rejects pins and holds without the rail's own thumbnail.
        if (pickedNoPoster) return { body: null, evicted: [], error: NO_POSTER_ERROR };
        return planChange(rail, { type: pickMode, seriesId: pickedId, position: pickPosition });
    }, [rail, pickedId, pickMode, pickPosition, pickedNoPoster]);

    const submitPick = () => {
        if (pickedId === null) {
            toast.error('Select a series.');
            return;
        }
        const title = picked.title || infoOf(pickedId).title;
        let saving;
        if (pickMode === 'exclude') {
            saving = commit(`exclude-${pickedId}`, { type: 'exclude', seriesId: pickedId }, `"${title}" excluded from the rail.`);
        } else if (pickMode === 'pin') {
            saving = pinSeries(pickedId, pickPosition);
        } else {
            saving = holdSeries(pickedId, pickPosition, holdById.get(pickedId)?.position === pickPosition);
        }
        saving.then((ok) => { if (ok) closePicker(); });
    };

    const statusOf = (seriesId) => {
        const pinPosition = pinPositionOf(rail, seriesId);
        if (pinPosition !== null) return { label: `Pinned #${pinPosition}`, className: 'is-picked' };
        const hold = holdById.get(seriesId);
        if (hold) return { label: `Held #${hold.position}`, className: 'is-held' };
        if (excludedSet.has(seriesId)) return { label: 'Excluded', className: 'is-excluded' };
        const live = previewById.get(seriesId);
        if (live) return { label: `In rail #${live.position}`, className: '' };
        return null;
    };

    const renderSlotChips = () => {
        const allowed = pickMode === 'pin' ? pinPositions(rail, pickedId) : holdPositions(rail, pickedId);

        return (
            <div className="nohc-slots">
                {Array.from({ length: RAIL_SIZE }, (_, i) => {
                    const slot = i + 1;
                    const available = allowed.includes(slot);
                    const pinHere = pinAt(rail, slot);
                    const heldHere = holdByPosition.get(slot);

                    let title = `Position ${slot}`;
                    let icon = null;
                    if (pinHere && pinHere.seriesId === pickedId) {
                        title = pickMode === 'pin' ? 'Current pin position' : 'Current pin — becomes a 15-day hold';
                        icon = 'fa-thumbtack';
                    } else if (pinHere) {
                        title = available
                            ? `Swap with the pin on ${infoOf(pinHere.seriesId).title}`
                            : `Pinned: ${infoOf(pinHere.seriesId).title} — unpin it first`;
                        icon = 'fa-thumbtack';
                    } else if (heldHere && heldHere.seriesId === pickedId) {
                        title = pickMode === 'pin'
                            ? 'Current hold — becomes a permanent pin'
                            : `Current hold — restarts the ${HOLD_DAYS} days`;
                        icon = 'fa-hourglass-half';
                    } else if (heldHere) {
                        title = `Ends the hold on ${infoOf(heldHere.seriesId).title}`;
                        icon = 'fa-hourglass-half';
                    }

                    return (
                        <button
                            type="button"
                            key={slot}
                            className={`nohc-slot${slot === pickPosition ? ' is-selected' : ''}${!available ? ' is-taken' : ''}`}
                            disabled={!available}
                            title={title}
                            onClick={() => setPickPosition(slot)}
                        >
                            {icon && <i className={`fas ${icon}`}></i>}
                            {slot}
                        </button>
                    );
                })}
            </div>
        );
    };

    // ─── Render ──────────────────────────────────────────────────────────────

    const renderPreviewRow = (slot) => {
        const item = previewByPosition.get(slot);
        if (!item) {
            return (
                <div className="cc-row nohc-row-empty" key={`empty-${slot}`}>
                    <span className="cc-position-badge">#{slot}</span>
                    <span className="cc-title">Empty — no more live series to auto-fill.</span>
                </div>
            );
        }

        const hold = holdById.get(item.seriesId);
        const isHeld = Boolean(hold) || item.isHeld;
        const isPinned = !isHeld && (pinnedSet.has(item.seriesId) || item.isPinned);
        const title = item.title || infoOf(item.seriesId).title;
        const thumbnail = item.thumbnail || infoOf(item.seriesId).thumbnail;
        const moved = movedIds.has(item.seriesId);
        // A pin from this row is only ever sent for THIS slot — never a rewritten
        // one. On a server that stores pins as a block from #1 the slot may not be
        // pinnable at all; then the button is disabled and says so, rather than
        // quietly pinning the series at a different rank.
        const pinTitle = `Pin permanently at #${slot}`;

        let tag;
        if (isHeld) {
            tag = (
                <span className="nohc-tag is-held" title={`${HOLD_DAYS}-day hold`}>
                    <i className="fas fa-hourglass-half me-1"></i>
                    {moved ? `${HOLD_DAYS}-day hold · starts on save` : holdLabel(hold?.expiresAt || item.expiresAt)}
                </span>
            );
        } else if (isPinned) {
            tag = <span className="nohc-tag is-pinned"><i className="fas fa-thumbtack me-1"></i>Pinned</span>;
        } else {
            tag = <span className="nohc-tag"><i className="fas fa-wand-magic-sparkles me-1"></i>Auto</span>;
        }

        return (
            <SortableRailRow
                key={item.seriesId}
                seriesId={item.seriesId}
                allowEdit={allowEdit}
                busy={busy}
                className={`${isHeld ? 'nohc-row-held' : isPinned ? 'nohc-row-pinned' : ''}${moved ? ' nohc-row-moved' : ''}`}
            >
                <span className="cc-position-badge">#{slot}</span>
                <Thumb src={thumbnail} />
                <span className="nohc-row-text">
                    <span className="cc-title" title={title}>{title}</span>
                    {isDate(item.publishAt) && (
                        <small title={formatDateTime(item.publishAt)}>Live since {formatDate(item.publishAt)}</small>
                    )}
                </span>
                {tag}
                {moved && <span className="nohc-tag is-unsaved">Unsaved</span>}

                {allowEdit && (
                    <div className="nohc-row-actions">
                        {isHeld && (
                            <>
                                <button
                                    type="button"
                                    className="nohc-icon-btn"
                                    title={`Make permanent — pin at #${slot}`}
                                    disabled={editLocked}
                                    onClick={() => pinSeries(item.seriesId, slot)}
                                >
                                    <i className="fas fa-thumbtack"></i>
                                </button>
                                <button
                                    type="button"
                                    className="nohc-icon-btn"
                                    title="Release the hold — the slot goes back to auto-fill"
                                    disabled={editLocked}
                                    onClick={() => releaseHold(item.seriesId)}
                                >
                                    <i className="fas fa-hourglass-end"></i>
                                </button>
                            </>
                        )}
                        {isPinned && (
                            <button
                                type="button"
                                className="nohc-icon-btn"
                                title="Unpin — the slot goes back to auto-fill"
                                disabled={editLocked}
                                onClick={() => unpinSeries(item.seriesId)}
                            >
                                <UnpinIcon />
                            </button>
                        )}
                        {!isHeld && !isPinned && (
                            <>
                                <button
                                    type="button"
                                    className="nohc-icon-btn"
                                    title={`Hold at #${slot} for ${HOLD_DAYS} days`}
                                    disabled={editLocked}
                                    onClick={() => holdSeries(item.seriesId, slot)}
                                >
                                    <i className="fas fa-hourglass-half"></i>
                                </button>
                                <button
                                    type="button"
                                    className="nohc-icon-btn"
                                    title={pinTitle}
                                    disabled={editLocked}
                                    onClick={() => pinSeries(item.seriesId, slot)}
                                >
                                    <i className="fas fa-thumbtack"></i>
                                </button>
                            </>
                        )}
                        <button
                            type="button"
                            className="cc-remove"
                            title="Exclude — remove from the rail and keep it out of auto-fill"
                            disabled={editLocked}
                            onClick={() => excludeSeries(item.seriesId)}
                        >
                            <i className="fas fa-ban"></i>
                        </button>
                    </div>
                )}
            </SortableRailRow>
        );
    };

    const pickerTitle = picked ? (picked.title || infoOf(pickedId).title) : '';
    const confirmLabel = { hold: `Hold for ${HOLD_DAYS} days`, pin: 'Pin series', exclude: 'Exclude series' }[pickMode];

    return (
        <>
            <div className="cc-page nohc-page">
                <div className="cc-toolbar">
                    <div className="cc-intro">
                        <h5>Recent Releases Rail</h5>
                        <p>
                            Fills itself with the latest {RAIL_SIZE} series to go live, newest first.
                            Drag rows to change positions, then Save &amp; Publish. Hold a series in place
                            for {HOLD_DAYS} days, pin it permanently at a position, or exclude it from the
                            rail — those buttons go live as soon as you click them.
                        </p>
                    </div>

                    <div className="cc-actions">
                        {refreshing && <i className="fas fa-spinner fa-spin nohc-sync" title="Refreshing"></i>}
                        {allowEdit && draft && (
                            <>
                                <button
                                    type="button"
                                    className="btn cc-btn-ghost"
                                    onClick={discardMoves}
                                    disabled={busy}
                                >
                                    <i className="fas fa-undo me-2"></i>Discard
                                </button>
                                <button
                                    type="button"
                                    className="btn upgrade-btn"
                                    onClick={saveMoves}
                                    disabled={busy}
                                >
                                    {busyKey === 'save-moves'
                                        ? <><i className="fas fa-spinner fa-spin me-2"></i>Saving...</>
                                        : <><i className="fas fa-save me-2"></i>Save &amp; Publish</>}
                                </button>
                            </>
                        )}
                        {allowEdit && !draft && (
                            <>
                                <button
                                    type="button"
                                    className="btn cc-btn-ghost nohc-btn-danger"
                                    onClick={resetRail}
                                    disabled={editLocked || loading || apiMissing || !hasOverrides}
                                    title={hasOverrides ? 'Clear every pin, hold and exclusion' : 'Nothing to reset'}
                                >
                                    <i className="fas fa-rotate-left me-2"></i>Reset
                                </button>
                                <button
                                    type="button"
                                    className="btn cc-btn-ghost"
                                    onClick={() => openPicker()}
                                    disabled={editLocked || loading || apiMissing}
                                >
                                    <i className="fas fa-plus me-2"></i>Add Series
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: 'var(--first-color)' }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : apiMissing ? (
                    <div className="cc-empty">
                        <i className="fas fa-plug-circle-xmark"></i>
                        <h5>This rail isn't available on the server yet</h5>
                        <p>
                            The API answered 404 for <code>/admin/v1/curation/curate-collections?curation_type={CURATION_TYPE}</code>.
                            It will work once the backend change is deployed.
                        </p>
                        <button type="button" className="btn cc-btn-ghost mt-3" onClick={() => fetchData(true)}>
                            <i className="fas fa-rotate-right me-2"></i>Try again
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="cc-config nohc-config">
                            <div className="cc-field">
                                <label htmlFor="nohc-name">Rail Name</label>
                                <div className="nohc-name-line">
                                    <input
                                        id="nohc-name"
                                        type="text"
                                        maxLength={NAME_MAX_LENGTH}
                                        value={nameDraft}
                                        disabled={!allowEdit || editLocked}
                                        placeholder={DEFAULT_NAME}
                                        onChange={(e) => setNameDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && nameDirty) {
                                                e.preventDefault();
                                                handleRename();
                                            }
                                        }}
                                    />
                                    {allowEdit && nameDirty && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn cc-btn-ghost"
                                                onClick={() => setNameDraft(rail.name || DEFAULT_NAME)}
                                                disabled={editLocked}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="btn upgrade-btn"
                                                onClick={handleRename}
                                                disabled={editLocked || Boolean(nameError)}
                                            >
                                                {busyKey === 'rename'
                                                    ? <i className="fas fa-spinner fa-spin"></i>
                                                    : 'Rename'}
                                            </button>
                                        </>
                                    )}
                                </div>
                                <small className={nameDirty && nameError ? 'nohc-error-text' : undefined}>
                                    {nameDirty && nameError
                                        ? nameError
                                        : `Shown as the rail heading in the app and on this tab. ${trimmedName.length}/${NAME_MAX_LENGTH}`}
                                </small>
                            </div>

                            <div className="cc-field cc-field-toggle">
                                <label htmlFor="nohc-active">Visible in app</label>
                                <div className="cc-toggle-line">
                                    <label className="cc-switch">
                                        <input
                                            id="nohc-active"
                                            type="checkbox"
                                            checked={rail.isActive}
                                            disabled={!allowEdit || editLocked}
                                            onChange={(e) => toggleActive(e.target.checked)}
                                        />
                                        <span className="cc-slider"></span>
                                    </label>
                                    <span className={`cc-state ${rail.isActive ? 'is-on' : ''}`}>
                                        {rail.isActive ? 'On' : 'Off'}
                                    </span>
                                </div>
                                <small>Off hides the rail from the home page; the preview below still shows what it would be.</small>
                            </div>

                            <div className="cc-field cc-field-count">
                                <label>Overrides</label>
                                <div className="cc-slot-track nohc-track">
                                    {Array.from({ length: RAIL_SIZE }, (_, i) => {
                                        const slot = i + 1;
                                        const row = previewByPosition.get(slot);
                                        const held = holdByPosition.has(slot) || row?.isHeld;
                                        const pinned = !held && (Boolean(pinAt(view, slot)) || row?.isPinned);
                                        return (
                                            <span
                                                key={slot}
                                                className={`cc-slot-pip${pinned ? ' is-filled' : ''}${held ? ' is-held' : ''}`}
                                                title={`#${slot}: ${held ? '15-day hold' : pinned ? 'pinned' : 'auto-fill'}`}
                                            />
                                        );
                                    })}
                                </div>
                                <div className="nohc-legend">
                                    <span><i className="nohc-dot is-pinned"></i>{view.pins.length} pinned</span>
                                    <span><i className="nohc-dot is-held"></i>{view.holds.length} on hold</span>
                                    <span><i className="nohc-dot"></i>{view.excludedIds.length} excluded</span>
                                </div>
                            </div>
                        </div>

                        {draft && (
                            <div className="cc-note">
                                <i className="fas fa-circle-exclamation me-2"></i>
                                <span>
                                    {moves.length} unsaved position change{moves.length === 1 ? '' : 's'}:{' '}
                                    {moves.map((m) => `"${infoOf(m.seriesId).title}" → #${m.position}${m.kind === 'pin' ? ' (pinned)' : ` (${HOLD_DAYS}-day hold)`}`).join(', ')}.
                                    Click <strong className="mx-1">Save &amp; Publish</strong> to make them live, or Discard.
                                    Other edits are locked until then.
                                </span>
                            </div>
                        )}

                        {!rail.isActive && (
                            <div className="cc-note cc-note-muted">
                                <i className="fas fa-eye-slash me-2"></i>
                                This rail is switched off, so it is not showing in the app. The preview below is what it would show.
                            </div>
                        )}

                        <div className="nohc-grid">
                            <section>
                                <h6 className="nohc-heading">What users see now</h6>
                                <div className="cc-list">
                                    {viewPreview.length === 0 && !hasOverrides ? (
                                        <div className="cc-empty">
                                            <i className="fas fa-bolt"></i>
                                            <h5>The rail is empty</h5>
                                            <p>It fills itself as soon as a series goes live.</p>
                                        </div>
                                    ) : (
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext
                                                items={viewPreview.map((p) => p.seriesId)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {Array.from({ length: RAIL_SIZE }, (_, i) => renderPreviewRow(i + 1))}
                                            </SortableContext>
                                        </DndContext>
                                    )}
                                </div>
                                {allowEdit && viewPreview.length > 1 && (
                                    <p className="nohc-side-hint">
                                        Drag a row onto another to move it there, then click Save &amp; Publish.
                                        {' '}A pin stays pinned (dropping it on another pin swaps them);
                                        {' '}any other series is held there for {HOLD_DAYS} days from when you save.
                                    </p>
                                )}
                                {isDate(updatedAt) && (
                                    <p className="cc-meta">Last saved {formatDateTime(updatedAt)}.</p>
                                )}
                            </section>

                            <aside>
                                <h6 className="nohc-heading">Pinned · permanent ({view.pins.length})</h6>
                                {view.pins.length === 0 ? (
                                    <p className="nohc-side-empty">Nothing pinned.</p>
                                ) : (
                                    <>
                                        <div className="nohc-side-list">
                                            {view.pins.map(({ seriesId, position }) => {
                                                const info = infoOf(seriesId);
                                                return (
                                                    <div className="nohc-side-row" key={seriesId}>
                                                        <span className="nohc-pos">#{position}</span>
                                                        <Thumb src={info.thumbnail} small />
                                                        <span className="cc-picker-title" title={info.title}>{info.title}</span>
                                                        {allowEdit && (
                                                            <button
                                                                type="button"
                                                                className="nohc-icon-btn"
                                                                title="Unpin — the slot goes back to auto-fill"
                                                                disabled={editLocked}
                                                                onClick={() => unpinSeries(seriesId)}
                                                            >
                                                                <UnpinIcon />
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <p className="nohc-side-hint">
                                            Each pin stays at its own position; auto-fill flows around it.
                                        </p>
                                    </>
                                )}

                                <h6 className="nohc-heading mt-4">{HOLD_DAYS}-day holds ({view.holds.length})</h6>
                                {view.holds.length === 0 ? (
                                    <p className="nohc-side-empty">No series is being held.</p>
                                ) : (
                                    <div className="nohc-side-list">
                                        {view.holds.map((h) => {
                                            const info = infoOf(h.seriesId);
                                            const left = holdTimeLeft(h.expiresAt);
                                            return (
                                                <div className="nohc-side-row" key={h.seriesId}>
                                                    <span className="nohc-pos is-held">#{h.position}</span>
                                                    <Thumb src={info.thumbnail} small />
                                                    <span className="nohc-side-text">
                                                        <span className="cc-picker-title" title={info.title}>{info.title}</span>
                                                        <small className={left?.expired ? 'is-expired' : undefined}>
                                                            {holdLabel(h.expiresAt)}
                                                        </small>
                                                    </span>
                                                    {allowEdit && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="nohc-icon-btn"
                                                                title="Release the hold — the slot goes back to auto-fill"
                                                                disabled={editLocked}
                                                                onClick={() => releaseHold(h.seriesId)}
                                                            >
                                                                <i className="fas fa-hourglass-end"></i>
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <h6 className="nohc-heading mt-4">Excluded ({view.excludedIds.length})</h6>
                                {view.excludedIds.length === 0 ? (
                                    <p className="nohc-side-empty">No series is excluded. Use the <i className="fas fa-ban"></i> on a row to keep it out of the rail.</p>
                                ) : (
                                    <div className="nohc-side-list">
                                        {view.excludedIds.map((id) => {
                                            const info = infoOf(id);
                                            return (
                                                <div className="nohc-side-row" key={id}>
                                                    <Thumb src={info.thumbnail} small />
                                                    <span className="cc-picker-title" title={info.title}>{info.title}</span>
                                                    {allowEdit && (
                                                        <button
                                                            type="button"
                                                            className="btn cc-btn-ghost nohc-restore"
                                                            title="Restore — it can be auto-filled again"
                                                            disabled={editLocked}
                                                            onClick={() => restoreSeries(id)}
                                                        >
                                                            {busyKey === `restore-${id}`
                                                                ? <i className="fas fa-spinner fa-spin"></i>
                                                                : <><i className="fas fa-rotate-left me-1"></i>Restore</>}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <p className="cc-meta">
                                    A hold is released at the first go-live after its {HOLD_DAYS} days are up.
                                    A series that is unpublished or deleted drops out of every list automatically.
                                </p>
                            </aside>
                        </div>
                    </>
                )}
            </div>

            {/* Portalled to <body> with its own backdrop: the shared .overlay/.common-popup
                sit at z-index 4-5, underneath the fixed top nav (100). */}
            {showPicker && createPortal(
                <>
                    <div className="nohc-backdrop" onClick={() => { if (!busy) closePicker(); }} />
                    <div className="common-popup add-series-popup active nohc-modal" role="dialog" aria-modal="true">
                        <div className="edit-heading d-flex align-items-center justify-content-between">
                            <h3>Add Series to {rail.name || DEFAULT_NAME}</h3>
                            <button className="common-close edit-close-btn" onClick={closePicker}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18">
                                    <path fill="#ffffff"
                                        d="M19.95 16.75l-.05-.4-1.2-1-5.2-4.2c-.1-.05-.3-.2-.6-.5l-.7-.55c-.15-.1-.5-.45-1-1.1l-.1-.1c.2-.15.4-.35.6-.55l1.95-1.85 1.1-1c1-1 1.7-1.65 2.1-1.9l.5-.35c.4-.25.65-.45.75-.45.2-.15.45-.35.65-.6s.3-.5.3-.7l-.3-.65c-.55.2-1.2.65-2.05 1.35-.85.75-1.65 1.55-2.5 2.5-.8.9-1.6 1.65-2.4 2.3-.8.65-1.4.95-1.9 1-.15 0-1.5-1.05-4.1-3.2C3.1 2.6 1.45 1.2.7.55L.45.1c-.1.05-.2.15-.3.3C.05.55 0 .7 0 .85l.05.35.05.4 1.2 1 5.2 4.15c.1.05.3.2.6.5l.7.6c.15.1.5.45 1 1.1l.1.1c-.2.15-.4.35-.6.55l-1.95 1.85-1.1 1c-1 1-1.7 1.65-2.1 1.9l-.5.35c-.4.25-.65.45-.75.45-.25.15-.45.35-.65.6-.15.3-.25.55-.25.75l.3.65c.55-.2 1.2-.65 2.05-1.35.85-.75 1.65-1.55 2.5-2.5.8-.9 1.6-1.65 2.4-2.3.8-.65 1.4-.95 1.9-1 .15 0 1.5 1.05 4.1 3.2 2.6 2.15 4.3 3.55 5.05 4.2l.2.45c.1-.05.2-.15.3-.3.1-.15.15-.3.15-.45z">
                                    </path>
                                </svg>
                            </button>
                        </div>

                        <div className="edit-inner nohc-picker">
                            <div className="nohc-step-label">1. Choose an action</div>
                            <div className="nohc-modes">
                                {[
                                    { key: 'hold', icon: 'fa-hourglass-half', title: `Hold for ${HOLD_DAYS} days`, hint: 'Fixed at a position, then released.' },
                                    { key: 'pin', icon: 'fa-thumbtack', title: 'Pin permanently', hint: 'Stays until you unpin it.' },
                                    { key: 'exclude', icon: 'fa-ban', title: 'Exclude from rail', hint: 'Removed and kept out of auto-fill.', danger: true },
                                ].map((mode) => (
                                    <button
                                        type="button"
                                        key={mode.key}
                                        className={`nohc-mode${mode.danger ? ' is-danger' : ''}${pickMode === mode.key ? ' is-active' : ''}`}
                                        onClick={() => changeMode(mode.key)}
                                    >
                                        <span className="nohc-mode-icon"><i className={`fas ${mode.icon}`}></i></span>
                                        <span className="nohc-mode-text">
                                            <strong>{mode.title}</strong>
                                            <small>{mode.hint}</small>
                                        </span>
                                        <i className={`fas ${pickMode === mode.key ? 'fa-circle-check' : 'fa-circle'} nohc-mode-check`}></i>
                                    </button>
                                ))}
                            </div>

                            {pickMode !== 'exclude' && (
                                <>
                                    <div className="nohc-step-label">2. Pick a position</div>
                                    {renderSlotChips()}
                                    <p className="nohc-picker-hint">
                                        {pickMode === 'hold'
                                            ? `The series stays at this position for ${HOLD_DAYS} days while newer releases flow around it. Pinned positions can't be held.`
                                            : 'The series stays at exactly this position until you unpin it; auto-fill flows around it. Moving a pin onto another pin swaps the two.'}
                                    </p>
                                </>
                            )}

                            <div className="nohc-step-label">{pickMode === 'exclude' ? '2' : '3'}. Select a series</div>
                            {picked && (
                                // A new search can scroll the chosen row out of the
                                // results, so the selection is cleared from here too.
                                <div className="nohc-picked">
                                    <Thumb src={picked.thumbnail || infoOf(pickedId).thumbnail} small />
                                    <span className="cc-picker-title" title={pickerTitle}>{pickerTitle}</span>
                                    <button
                                        type="button"
                                        className="nohc-icon-btn"
                                        title="Deselect this series"
                                        disabled={busy}
                                        onClick={clearPick}
                                    >
                                        <i className="fas fa-xmark"></i>
                                    </button>
                                </div>
                            )}
                            <div className="nohc-search">
                                <i className="fas fa-magnifying-glass"></i>
                                <input
                                    type="text"
                                    placeholder="Search series by title..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="cc-picker-list nohc-results">
                                {searchLoading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border" style={{ color: 'var(--first-color)' }} role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : results.length === 0 ? (
                                    <p className="cc-picker-empty">
                                        {hiddenNoPoster > 0
                                            ? `${hiddenNoPoster} series ${hiddenNoPoster === 1 ? 'matches' : 'match'}, but ${hiddenNoPoster === 1 ? 'it has' : 'they have'} no New on Hike Cinema poster yet. Add the poster on the series first.`
                                            : searchTerm.trim()
                                                ? 'No active series matches that search.'
                                                : 'No active series available.'}
                                    </p>
                                ) : (
                                    <>
                                        {results.map((row) => {
                                            const status = statusOf(row.seriesId);
                                            const selected = pickedId === row.seriesId;
                                            return (
                                                <button
                                                    type="button"
                                                    className={`nohc-result${selected ? ' is-selected' : ''}`}
                                                    key={row.seriesId}
                                                    aria-pressed={selected}
                                                    title={selected ? 'Click again to deselect' : undefined}
                                                    onClick={() => pickSeries(row)}
                                                >
                                                    <Thumb src={row.thumbnail} small />
                                                    <span className="cc-picker-title">{row.title || `Series #${row.seriesId}`}</span>
                                                    {status && (
                                                        <span className={`cc-picker-badge ${status.className}`}>{status.label}</span>
                                                    )}
                                                    <i className={`fas ${selected ? 'fa-circle-check' : 'fa-circle'} nohc-result-check`}></i>
                                                </button>
                                            );
                                        })}

                                        {hasMore && (
                                            <button
                                                type="button"
                                                className="cc-load-more"
                                                onClick={() => runSearch(searchTerm, searchPage + 1, true)}
                                                disabled={loadingMore}
                                            >
                                                {loadingMore ? 'Loading...' : 'Load more series'}
                                            </button>
                                        )}
                                        {hiddenNoPoster > 0 && (
                                            <p className="nohc-picker-hint mb-0">
                                                {hiddenNoPoster} series hidden — no New on Hike Cinema poster yet.
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>

                            {livePlan?.error && (
                                <p className="nohc-picker-note is-error">
                                    <i className="fas fa-circle-exclamation me-2"></i>{livePlan.error}
                                </p>
                            )}
                            {!livePlan?.error && livePlan?.evicted.length > 0 && (
                                <p className="nohc-picker-note">
                                    <i className="fas fa-hourglass-end me-2"></i>
                                    Ends the {HOLD_DAYS}-day hold on{' '}
                                    {livePlan.evicted.map((h) => `${infoOf(h.seriesId).title} (#${h.position})`).join(', ')}.
                                </p>
                            )}
                        </div>

                        <div className="nohc-picker-footer">
                            <span className="nohc-summary">
                                {!picked && 'No series selected'}
                                {picked && pickMode === 'exclude' && <>Exclude <strong>{pickerTitle}</strong> from the rail</>}
                                {picked && pickMode === 'pin' && (
                                    <>Pin <strong>{pickerTitle}</strong>{pickPosition ? <> at <strong>#{pickPosition}</strong></> : ''}</>
                                )}
                                {picked && pickMode === 'hold' && (
                                    <>Hold <strong>{pickerTitle}</strong>{pickPosition ? <> at <strong>#{pickPosition}</strong></> : ''} for {HOLD_DAYS} days</>
                                )}
                            </span>
                            <div className="nohc-footer-actions">
                                <button type="button" className="btn cc-btn-ghost" onClick={closePicker} disabled={busy}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn upgrade-btn"
                                    onClick={submitPick}
                                    disabled={!picked || busy || Boolean(livePlan?.error)}
                                >
                                    {busy ? <i className="fas fa-spinner fa-spin"></i> : confirmLabel}
                                </button>
                            </div>
                        </div>
                    </div>
                </>,
                document.body,
            )}

            <CuratedCollectionStyles />
            <NewOnHikeCinemaStyles />
        </>
    );
};

export const NewOnHikeCinemaStyles = () => (
    <style>{`
        .nohc-config { grid-template-columns: minmax(260px, 2fr) minmax(170px, 1fr) minmax(220px, 1fr); }
        .nohc-name-line { display: flex; gap: 8px; align-items: center; }
        .nohc-name-line .btn { flex: none; padding: 8px 14px; }
        .nohc-error-text { color: var(--error-color) !important; }
        .nohc-sync { color: var(--text-color); }

        .nohc-track { margin-top: 6px; }
        .cc-slot-pip.is-held { background: var(--price-color); }
        .nohc-legend { display: flex; gap: 12px; flex-wrap: wrap; font-size: 11.5px; color: var(--text-color); }
        .nohc-legend span { display: inline-flex; align-items: center; gap: 5px; }
        .nohc-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--border-color); display: inline-block; }
        .nohc-dot.is-pinned { background: var(--first-color); }
        .nohc-dot.is-held { background: var(--price-color); }

        .nohc-grid {
            display: grid; grid-template-columns: minmax(0, 3fr) minmax(280px, 2fr);
            gap: 24px; align-items: start;
        }
        .nohc-heading {
            font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
            color: var(--text-color); font-weight: 600; margin: 0 0 10px;
        }
        .nohc-grid .cc-list { max-width: none; }
        .nohc-row-pinned { border-color: rgba(var(--first-color-rgb), 0.5); }
        .nohc-row-held { border-color: rgba(255, 145, 57, 0.5); }
        .nohc-row-text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
        .nohc-row-text .cc-title { flex: none; }
        .nohc-row-text small { font-size: 11.5px; color: var(--text-color); }
        .nohc-grid .cc-row.is-dragging {
            border-color: var(--first-color); box-shadow: var(--shadow-glow);
            position: relative; z-index: 2;
        }
        .nohc-row-moved { border-style: dashed; border-color: var(--first-color); }
        .nohc-tag.is-unsaved { color: var(--first-color); border-color: var(--first-color); border-style: dashed; }
        .nohc-row-empty { opacity: 0.55; border-style: dashed; }
        .nohc-row-empty .cc-title { font-weight: 400; font-size: 13px; color: var(--text-color); }
        .nohc-tag {
            flex: none; font-size: 11px; padding: 2px 8px; border-radius: 10px; white-space: nowrap;
            border: 1px solid var(--border-color); color: var(--text-color);
        }
        .nohc-tag.is-pinned {
            color: var(--first-color); border-color: var(--first-color);
            background: rgba(var(--first-color-rgb), 0.12); font-weight: 600;
        }
        .nohc-tag.is-held {
            color: var(--price-color); border-color: rgba(255, 145, 57, 0.6);
            background: rgba(255, 145, 57, 0.1); font-weight: 600;
        }
        .nohc-row-actions { display: flex; gap: 6px; flex: none; }
        .nohc-icon-btn {
            flex: none; background: transparent; border: 1px solid var(--border-color);
            color: var(--text-color); width: 30px; height: 30px; border-radius: 50%;
            display: flex; align-items: center; justify-content: center; font-size: 12px;
        }
        .nohc-icon-btn:hover:not(:disabled) { border-color: var(--first-color); color: var(--first-color); }
        .nohc-icon-btn:disabled, .cc-remove:disabled { opacity: 0.4; }
        .nohc-unpin { width: 1em; height: 1em; line-height: 1em; font-size: 13px; }
        .nohc-unpin .fa-stack-1x { line-height: 1em; font-size: 1em; }

        .nohc-side-list { display: flex; flex-direction: column; gap: 6px; }
        .nohc-side-row {
            display: flex; align-items: center; gap: 10px; padding: 6px 10px;
            background: var(--glass-bg); border: 1px solid var(--border-color);
            border-radius: var(--radius-sm); font-size: 14px; color: var(--body-text-color);
        }
        .nohc-side-row.is-dragging {
            border-color: var(--first-color); box-shadow: var(--shadow-glow);
            position: relative; z-index: 2;
        }
        .nohc-side-row .cc-handle { padding: 2px; font-size: 14px; }
        .nohc-side-text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
        .nohc-side-text .cc-picker-title { flex: none; }
        .nohc-side-text small { font-size: 11.5px; color: var(--price-color); }
        .nohc-side-text small.is-expired { color: var(--text-color); }
        .nohc-pos {
            flex: none; min-width: 34px; text-align: center; font-size: 12px; font-weight: 700;
            color: var(--first-color); background: rgba(var(--first-color-rgb), 0.12);
            border-radius: var(--radius-sm); padding: 3px 6px;
        }
        .nohc-pos.is-held { color: var(--price-color); background: rgba(255, 145, 57, 0.12); }
        .nohc-side-empty { font-size: 13px; color: var(--text-color); margin: 0; }
        .nohc-side-hint { font-size: 11.5px; color: var(--text-color); margin: 6px 0 0; }
        .nohc-restore { flex: none; padding: 4px 10px; font-size: 12px; }

        /* Ghost buttons pick up the global blue .btn:hover fill; keep the label readable on it. */
        .nohc-page .cc-btn-ghost:hover:not(:disabled),
        .nohc-modal .cc-btn-ghost:hover:not(:disabled) { color: #fff; }
        .nohc-page .nohc-btn-danger:hover:not(:disabled) {
            background: var(--error-color); border-color: var(--error-color);
        }

        /* ── Picker modal ── above the fixed top nav (z-index 100). */
        .nohc-backdrop {
            position: fixed; inset: 0; z-index: 1040;
            background: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        }
        .common-popup.nohc-modal {
            z-index: 1050; max-width: 680px; width: calc(100% - 32px);
            max-height: calc(100vh - 48px);
            display: flex; flex-direction: column;
        }
        .nohc-modal .edit-heading { flex: none; }
        .nohc-modal .edit-inner.nohc-picker {
            flex: 1 1 auto; min-height: 0; max-height: none;
            overflow-y: auto; padding-top: 4px;
        }
        .nohc-modal .nohc-picker-footer { flex: none; }
        .nohc-step-label {
            font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em;
            font-weight: 600; color: var(--text-color); margin: 14px 0 8px;
        }
        .nohc-step-label:first-child { margin-top: 0; }

        .nohc-modes { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
        .nohc-mode {
            display: flex; align-items: center; gap: 10px; text-align: left;
            padding: 12px; background: var(--glass-bg);
            border: 1px solid var(--border-color); border-radius: var(--radius-md);
            color: var(--body-text-color); transition: border-color 0.15s, background 0.15s;
        }
        .nohc-mode:hover { border-color: rgba(var(--first-color-rgb), 0.5); }
        .nohc-mode.is-active {
            border-color: var(--first-color); background: rgba(var(--first-color-rgb), 0.08);
        }
        .nohc-mode-icon {
            flex: none; width: 32px; height: 32px; border-radius: 50%;
            display: flex; align-items: center; justify-content: center; font-size: 13px;
            color: var(--first-color); background: rgba(var(--first-color-rgb), 0.12);
        }
        .nohc-mode.is-danger .nohc-mode-icon { color: var(--error-color); background: rgba(247, 64, 50, 0.12); }
        .nohc-mode.is-danger.is-active { border-color: var(--error-color); background: rgba(247, 64, 50, 0.08); }
        .nohc-mode-text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .nohc-mode-text strong { font-size: 13.5px; font-weight: 600; }
        .nohc-mode-text small { font-size: 11.5px; color: var(--text-color); line-height: 1.35; }
        .nohc-mode-check { flex: none; font-size: 15px; color: var(--border-color); }
        .nohc-mode.is-active .nohc-mode-check { color: var(--first-color); }
        .nohc-mode.is-danger.is-active .nohc-mode-check { color: var(--error-color); }

        .nohc-slots { display: grid; grid-template-columns: repeat(10, 1fr); gap: 6px; }
        .nohc-slot {
            display: flex; align-items: center; justify-content: center; gap: 4px;
            height: 36px; font-size: 14px; font-weight: 600;
            background: var(--glass-bg); color: var(--body-text-color);
            border: 1px solid var(--border-color); border-radius: var(--radius-sm);
        }
        .nohc-slot i { font-size: 9px; opacity: 0.8; }
        .nohc-slot:hover:not(:disabled) { border-color: var(--first-color); color: var(--first-color); }
        .nohc-slot.is-selected,
        .nohc-slot.is-selected:hover:not(:disabled) {
            background: var(--first-color); border-color: var(--first-color); color: #fff;
        }
        .nohc-slot.is-taken { opacity: 0.35; cursor: not-allowed; }
        .nohc-picker-hint { margin: 8px 0 0; font-size: 12px; color: var(--text-color); }
        .nohc-picker-note {
            display: flex; align-items: flex-start; margin: 12px 0 0; padding: 8px 12px;
            font-size: 12.5px; color: var(--price-color);
            background: rgba(255, 145, 57, 0.1); border: 1px solid rgba(255, 145, 57, 0.25);
            border-radius: var(--radius-sm);
        }
        .nohc-picker-note i { margin-top: 3px; }
        .nohc-picker-note.is-error {
            color: var(--error-color); background: rgba(247, 64, 50, 0.1); border-color: rgba(247, 64, 50, 0.25);
        }

        .nohc-search { position: relative; margin-bottom: 10px; }
        .nohc-search i {
            position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
            color: var(--text-color); font-size: 13px; pointer-events: none; z-index: 1;
        }
        .nohc-search input[type="text"] { padding: 10px 14px 10px 38px; }

        .nohc-results { max-height: 240px; margin-bottom: 0; }
        .nohc-result {
            display: flex; align-items: center; gap: 10px; width: 100%; text-align: left;
            padding: 6px 10px; margin: 0; background: transparent;
            border: 1px solid transparent; border-radius: var(--radius-sm);
            font-size: 14px; color: var(--body-text-color);
        }
        .nohc-result:hover { background: var(--sidebar-hover); }
        .nohc-result.is-selected {
            border-color: var(--first-color); background: rgba(var(--first-color-rgb), 0.1);
        }
        .nohc-result-check { flex: none; font-size: 15px; color: var(--border-color); }
        .nohc-picked {
            display: flex; align-items: center; gap: 10px;
            padding: 6px 10px; margin-bottom: 8px;
            border: 1px solid var(--first-color); border-radius: var(--radius-sm);
            background: rgba(var(--first-color-rgb), 0.1);
            font-size: 14px; color: var(--body-text-color);
        }
        .nohc-picked .cc-picker-title { flex: 1; }
        .nohc-result.is-selected .nohc-result-check { color: var(--first-color); }
        .cc-picker-badge.is-excluded { color: var(--error-color); border-color: var(--error-color); }
        .cc-picker-badge.is-held { color: var(--price-color); border-color: rgba(255, 145, 57, 0.6); }

        .nohc-picker-footer {
            display: flex; align-items: center; justify-content: space-between; gap: 12px;
            padding: 14px 24px; border-top: 1px solid var(--border-color);
        }
        .nohc-summary {
            flex: 1 1 auto; min-width: 0; font-size: 13px; color: var(--text-color);
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .nohc-summary strong { color: var(--body-text-color); }
        .nohc-footer-actions { display: flex; gap: 8px; flex: none; }
        .nohc-footer-actions .btn { padding: 8px 18px; }

        @media (max-width: 991px) { .nohc-grid { grid-template-columns: 1fr; } .nohc-config { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 767px) { .nohc-modes { grid-template-columns: 1fr; } }
        @media (max-width: 575px) {
            .nohc-config { grid-template-columns: 1fr; }
            .nohc-name-line { flex-wrap: wrap; }
            .nohc-slots { grid-template-columns: repeat(5, 1fr); }
            .nohc-picker-footer { flex-wrap: wrap; }
            .cc-row { flex-wrap: wrap; }
            .nohc-row-text { flex-basis: 100%; order: 4; }
            .nohc-row-text .cc-title { order: 0; }
        }
    `}</style>
);

export default NewOnHikeCinema;
